/**
Was muss konfiguriert werden?

Basis-Konfig:
Wann wird "Zoom-Button" aktiv? (3:00)
Wann wird automatisch Zoom gestartet bei "Beitreten"? (-3:00 bis +3:00)

individueller Timer:
Min-Fokus-Dauer (25:00)
Max-Fokus-Dauer (50:00)
Step-Größe (5:00)
Session-Schema-Name

Session-Schema(ta):
Schema-Name (zb "individuell" "standard", "xl", "xmas-greetings", "lärchenstunde")
Gesamt-Dauer (60:00, 90:00)
Timer-Grafik (URL)
Wie vielen Minuten entsprechen 360° in dieser Timer-Grafik? (auch 60:00, 90:00)
Phasen. Jeweils:
Name (für Anzeige unter dem Timer)
Farbe (für Kreissegment)
Dauer (der Phase, also zb 7:54, 50:09, 1:57)
Audio-Basisname ("intro", null, "outro")
Audio-Flavors/Sprecher/Varianten
hiervon wird immer eines zufällig gewählt, für die ganze Session, für alle Nutzer gleich, aber nicht immer jeden Tag um 10:30 die selbe Variante)
"_wiebke", "_ingrid", ... 
Basisname + Variante + ".mp3" (oder anderes Format) = Dateiname


Räume:
Raumname
Zeitplan
in aufsteigender Reihenfolge werden die Sessions für den Tag gelistet
jeweils:
Session-Schema-Name (-> Referenz auf oben, Phasen-Aufteilung. Wahrscheinlich erstmal immer gleich pro Raum)
Session-Name (-> Anzeige vor der Tür: "nächste Session: Lerchenstunde 9:15")
 */
export interface TWAConfig {
    /**
     * The time zone in which the sessions are scheduled.
     */
    timezone: string;
    zoom: {
        /**
         * e.g. "03:00" or "00:03:00" for the zoom-button to be visible 3 minutes before the session starts
         */
        buttonVisible_beforeSessionStart?: TimeString,
        /**
         * e.g. "03:00" or "00:03:00" for users to auto-join zoom when they join the room at most 3 minutes before the session starts
         */
        autoJoin_beforeSessionStart?: TimeString,
        /**
         * e.g. "03:00" or "00:03:00" for users to auto-join zoom when they join the room at most 3 minutes after the session starts
         */
        autoJoin_afterSessionStart?: TimeString,
    },
    /** a list of all formats that (each!) audio is available in. Default: [".mp3"]. Add e.g. ".aac" for better playback in Safari*/
    audioFormats: string[],
    individualTimer: IndivTimerSchema;
    /**
     * definitions of session schemas
     */
    sessionSchemas: {
        [schemaName: string]: SessionSchema;
    };
    /** definitions of room schemas */
    rooms: {
        [roomName: string]: RoomSchema;
    };
    startPage?: "lobby" | "roomlist" | "timer";
    lobbies: Lobbies;
}

export interface Lobbies {
    [lobbyName: string]: Lobby;
}

export interface Lobby {
    doors: LobbyDoor[];
}

export type LobbyDoor = LobbyDoorRoom | LobbyDoorLink;

export interface LobbyDoorRoom {
    roomName: string;
    url: string;
}

export const isLobbyDoorRoom = (door: LobbyDoor): door is LobbyDoorRoom => {
    return (door as LobbyDoorRoom).roomName !== undefined;
}

export interface LobbyDoorLink {
    /** URL to open */
    url: string;
    /** text of the "enter room" button */
    name: string;
    /**
     * color of the "enter room" button
     */
    color: string;
    /**
     * Text beneath the door image
     */
    info: string;
    /**
     * URL of the "door" image to display when the user is not in the room.
     */
    doorImage: string;
    /**
     * URL of the "door" image to display when the user is not in the room and hovers their mouse over the door image
     */
    doorImageHover?: string;
}

export interface IndivTimerSchema {
    sessionSchema: string,
    maxFocusDuration: TimeString,
    minFocusDuration: TimeString,
    defaultFocusDuration: TimeString,
    focusDurationStep: TimeString,
    pauseImages?: string[],
    color?: string;
}

export interface RoomSchema {
    /**
     * Used in the "enter room" and "leave room" button texts
     */
    displayName: string;
    /**
     * Color of the "enter room" button
     */
    color?: string;
    /**
     * Link to the zoom room
     */
    zoomLink?: string;
    schedule: ScheduledSession[];
    /**
     * A list of possible images to display when the user is in the room, but no session is running.
     */
    pauseImages?: string[];
    /**
     * URL of the "door" image to display when the user is not in the room.
     */
    doorImage?: string;
    /**
     * URL of the "door" image to display when the user is not in the room and hovers their mouse over the door image
     */
    doorImageHover?: string;
    /**
     * data used on the "/peekroom/{roomName}" page
     */
    peek?: Partial<PeekRoomInfo>;
}

export interface PeekRoomInfo {
    /** text to display in the "open room popup" button */
    buttonText: string;
    imagePause: string;
    imagePauseHover?: string;
    imageSession: string;
    imageSessionHover?: string;
}

export interface ScheduledSession {
    /** time-of-day when this sessions starts. in HH:MM (or HH:MM:SS) */
    startTime: TimeString;
    /** reference to sessionSchemas-keys */
    sessionSchema: string;
    /** used to display in the app */
    sessionName: string;
}

export interface SessionSchema {
    /**
     * e.g. "60:00" or "90:00
     */
    totalDuration: TimeString;
    /**
     * Which clock graphic should be used for this session schema?
     */
    graphic: ClockGraphic<TimeString>;
    /**
     * Which audio file options are available for this session schema? 
     * For each session, one is picked randomly.
     */
    audioFlavors?: string[];
    /**
     * Which phases are there in this session schema?
     */
    phases: PhaseDefinition[];
}

/** T either TimeString or number */
export interface ClockGraphic<T extends TimeString | number> {
    /**
     * Background image. should be a square with the center of the clock in the center of the image.
     */
    url: string;
    /**
     * (optional) Background image (overlay) with volume indicator. should be a square with the center of the clock in the center of the image. Maybe transparent.
     */
    urlWithVolume?: string;
    /**
     * e.g. "60:00" or "90:00"
     * (or converted to seconds)
     */
    fullCircleDuration: T;
    /**
     * radiusOuter in pixels of the RingSegments that should be displayed in the clock.
     * Default: 58
     */
    circleRadius?: number;
    /**
     * Color of the RingSegment that shows the passed time.
     */
    colorPassedTime?: string;
}

export interface PhaseDefinition {
    /** used to diplay in the app */
    name: string;
    /** used to diplay in the app */
    description?: string;
    imageInsteadOfClock?: string;
    /** color of ring segment. e.g. "#FF0000" or "twa-green" */
    color: string;
    /** duration in MM:SS (or HH:MM:SS) */
    duration: TimeString;
    /** duration in MM:SS (or HH:MM:SS) */
    maySkipAfter?: TimeString;
    /** e.g. "intro" or "outro" or null - will be extended with the flavor */
    audioBaseName: string | null;
    /** e.g. "intro" or "outro" or null - will not be extended with the flavor */
    audio: string | null;
    hiddenOnClock?: boolean;
    type?: PhaseType;
}

export type PhaseType = "audio" | "set-intention" | "focus" | "review-intention";

declare const nominalSymbol: unique symbol;
export type StrictNominal<N extends string, T> = T & { readonly [nominalSymbol]: N };

/**
 * A string defining a timespan or a time-of-day in the format "HH:MM:SS" (or "MM:SS" for durations or "HH:MM" for times-of-day)
 */
export type TimeString = StrictNominal<'TimeString', string>;

// export const DEFAULT_CONFIG: TWAConfig = import('./defaultConfig') as unknown as TWAConfig;